import {
  useAddOneListingToFavourites,
  useAddOneGroupingToFavourites,
  useRemoveOneListingFromFavourites,
  useRemoveOneGroupingFromFavourites,
} from 'app/network/listing';
import { Platform, Pressable } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import Animated, {
  interpolateColor,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { ShowErrors } from 'app/components/show-errors';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useEffect } from 'react';
import type { Listing } from 'app/types/listing';
import { isLoggedInAtom } from 'app/atoms';
import { optimisticFavouritesAtom as optimisticFavouriteAtom } from 'app/atoms/ui';
import { useAtom, useAtomValue } from 'jotai';
import RNHapticFeedback from 'react-native-haptic-feedback';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { Text } from 'app/design/primitives/text';
const theme = createDSTheme();
export interface FavouriteButtonProps {
  listing: Pick<Listing, 'id' | 'favourites' | 'grouping'>;
  count?: number;
  isFavouriteForced?: boolean;
  disabled?: boolean;
}

const HEART_PATH =
  'M5.89683 16.9102L13.6324 24.1334C14.0159 24.4915 14.5081 24.6705 15.0002 24.6705C15.4923 24.6705 15.9845 24.4915 16.368 24.1334L24.1036 16.9102C25.3216 15.7729 26 14.2284 26.0139 12.5614C26.0273 10.9525 25.4089 9.4326 24.2726 8.28156C23.1369 7.1311 21.6261 6.49347 20.0183 6.48615C18.7235 6.48037 17.4879 6.87913 16.4449 7.63967C15.8739 8.056 15.3876 8.55989 15.0002 9.13193C14.6128 8.55989 14.1265 8.056 13.5556 7.63967C12.5126 6.87914 11.2764 6.48037 9.98212 6.48615C8.37437 6.49347 6.8635 7.1311 5.72778 8.28156C4.5915 9.4326 3.97311 10.9525 3.98654 12.5614C4.00046 14.2284 4.67887 15.7729 5.89683 16.9102H5.89683Z';

function useFavouriteButton(listing: FavouriteButtonProps['listing']) {
  const [optimisticFavourites, setOptimisticFavourites] = useAtom(optimisticFavouriteAtom);
  const favouriteKey = listing.grouping?.id ?? listing.id;

  const {
    error: addListingError,
    mutate: addListingToFavourites,
    isPending: isAddingListing,
  } = useAddOneListingToFavourites();
  const {
    error: addGroupingError,
    mutate: addGroupingToFavourites,
    isPending: isAddingGrouping,
  } = useAddOneGroupingToFavourites();
  const {
    error: removeListingError,
    mutate: removeListingFromFavourites,
    isPending: isRemovingListing,
  } = useRemoveOneListingFromFavourites();
  const {
    error: removeGroupingError,
    mutate: removeGroupingFromFavourites,
    isPending: isRemovingGrouping,
  } = useRemoveOneGroupingFromFavourites();

  const isLoading = isAddingListing || isRemovingListing || isAddingGrouping || isRemovingGrouping;

  const optimisticFavourite = optimisticFavourites[favouriteKey];
  const cachedFavourite = listing.grouping
    ? (listing.grouping.groupingFavourites?.length ?? 0) > 0
    : (listing.favourites?.length ?? 0) > 0;
  const isFavourite = optimisticFavourite ?? cachedFavourite;

  const withAuthorize = useAuthorize('favourite');
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const onPress = () => {
    if (isLoading) return false; // Prevent multiple clicks while loading

    if (!isLoggedIn) {
      withAuthorize(() => undefined);
      return false;
    }

    const nextValue = !isFavourite;

    setOptimisticFavourites((prev) => ({
      ...prev,
      [favouriteKey]: nextValue,
    }));
    const rollbackOptimisticFavourite = () => {
      setOptimisticFavourites((currentFavourites) => {
        const remainingFavourites = Object.fromEntries(
          Object.entries(currentFavourites).filter(([key]) => key !== favouriteKey),
        );
        return isFavourite === cachedFavourite
          ? remainingFavourites
          : { ...remainingFavourites, [favouriteKey]: isFavourite };
      });
    };

    if (listing.grouping?.id) {
      if (nextValue) {
        addGroupingToFavourites({ groupingId: listing.grouping.id }, { onError: rollbackOptimisticFavourite });
      } else {
        removeGroupingFromFavourites(listing.grouping.id, { onError: rollbackOptimisticFavourite });
      }
    } else {
      if (nextValue) {
        addListingToFavourites({ listingId: listing.id }, { onError: rollbackOptimisticFavourite });
      } else {
        removeListingFromFavourites(listing.id, { onError: rollbackOptimisticFavourite });
      }
    }

    return true;
  };

  return {
    isFavourite,
    cachedFavourite,
    optimisticFavourite,
    canToggleFavourite: isLoggedIn && !isLoading,
    onPress,
    error: addListingError || removeListingError || addGroupingError || removeGroupingError,
  };
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const AnimatedPath = Animated.createAnimatedComponent(Path);

function FavouriteButton({ count, listing, isFavouriteForced, disabled: disabledProp }: FavouriteButtonProps) {
  const {
    isFavourite: isListingFavourite,
    cachedFavourite,
    optimisticFavourite,
    canToggleFavourite,
    onPress,
    error,
  } = useFavouriteButton(listing);
  const isFavourite = isFavouriteForced ?? isListingFavourite;
  const isDisabled = disabledProp ?? false;
  const { t } = useTranslation('accessibility');
  const scale = useSharedValue(1);
  const favouriteProgress = useSharedValue(isFavourite ? 1 : 0);
  const optimisticCountAdjustment =
    optimisticFavourite === undefined || optimisticFavourite === cachedFavourite ? 0 : optimisticFavourite ? 1 : -1;
  const displayedCount = count === undefined ? undefined : Math.max(0, count + optimisticCountAdjustment);

  useEffect(() => {
    favouriteProgress.set(withTiming(isFavourite ? 1 : 0, { duration: 220 }));
  }, [favouriteProgress, isFavourite]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.get() }],
  }));
  const animatedHeartProps = useAnimatedProps(() => {
    const stroke = interpolateColor(
      favouriteProgress.get(),
      [0, 1],
      [theme.colors['text.primary'], theme.colors['icon.error']],
    );
    const fill = interpolateColor(favouriteProgress.get(), [0, 1], ['transparent', theme.colors['icon.error']]);

    return {
      fill,
      stroke,
    };
  });
  const webHeartFill = isFavourite ? theme.colors['icon.error'] : 'transparent';
  const webHeartStroke = isFavourite ? theme.colors['icon.error'] : theme.colors['text.primary'];

  const handlePress = () => {
    if (isDisabled) {
      return;
    }
    if (!canToggleFavourite) {
      onPress();
      return;
    }

    const nextProgress = isFavourite ? 0 : 1;

    favouriteProgress.set(withTiming(nextProgress, { duration: 220 }));
    scale.set(withSequence(withTiming(0.75, { duration: 100 }), withSpring(1, { damping: 18, stiffness: 400 })));
    RNHapticFeedback.trigger('impactLight', {
      enableVibrateFallback: true,
      ignoreAndroidSystemSettings: false,
    });
    onPress();
  };

  return (
    <>
      <ShowErrors error={error} />
      <AnimatedPressable
        accessible
        accessibilityLabel={isFavourite ? t('favourite.remove') : t('favourite.add')}
        accessibilityRole="button"
        accessibilityState={{ disabled: isDisabled, selected: isFavourite }}
        accessibilityValue={displayedCount === undefined ? undefined : { text: displayedCount.toLocaleString() }}
        disabled={isDisabled}
        hitSlop={15}
        onPress={handlePress}
        style={[
          animatedStyle,
          displayedCount === undefined
            ? undefined
            : {
                alignItems: 'center',
                flexDirection: 'row',
                gap: 2,
              },
        ]}
      >
        <Svg height={30} width={30} viewBox="0 0 30 30">
          {Platform.OS === 'web' ? (
            <Path d={HEART_PATH} fill={webHeartFill} stroke={webHeartStroke} strokeWidth={1.5} />
          ) : (
            <AnimatedPath animatedProps={animatedHeartProps} d={HEART_PATH} strokeWidth={1.5} />
          )}
        </Svg>
        {displayedCount === undefined ? null : (
          <Text color="text.primary" fontWeight="700" numberOfLines={1} variant="bodySmall">
            {displayedCount.toLocaleString()}
          </Text>
        )}
      </AnimatedPressable>
    </>
  );
}

export { FavouriteButton, useFavouriteButton };
